import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { deleteInvitation } from "~/api/patients";
import { Button, Modal } from "~/ui";
import { errorToast, useToastStore } from "../../Shared/Toast";

interface DeleteInvitationConfirmationModalProps {
  isOpen: boolean;
  email: string;
  onClose: () => void;
}

export function DeleteInvitationConfirmationModal({
  isOpen,
  email,
  onClose,
}: DeleteInvitationConfirmationModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { pushToast } = useToastStore();

  const { mutate } = useMutation({
    mutationFn: deleteInvitation,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getPatientInvitations"],
      });
      onClose();
      void pushToast({
        type: "success",
        message: t("patients.delete_invitation_success"),
      });
    },
    onError: errorToast,
  });

  const onSubmit = () => {
    mutate(email);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("patients.delete_invitation")}
    >
      <p className="mb-8 text-left text-lg text-neutral-50 dark:text-neutral-80">
        {t("patients.delete_invitation_confirmation")}
        <span className="font-bold">{email}</span>?
      </p>

      <div className="flex w-full gap-3">
        <Button
          variant="outline"
          onClick={onClose}
          text={t("general.cancel")}
          className="flex grow"
        />

        <Button
          variant="filled"
          onClick={onSubmit}
          text={t("general.delete")}
          className="flex grow"
        />
      </div>
    </Modal>
  );
}
